<template>
    <div class="overview">
        <!-- Greeting -->
        <div class="overview__greeting">
            <h2 class="overview__greeting-title">
                {{ learnerName ? `${learnerName}'s Plan` : 'Learner Plan' }}
            </h2>
            <p class="overview__greeting-sub">
                Here's where {{ learnerFirstName || 'this learner' }} is at and what we recommend doing next.
            </p>
        </div>

        <div class="overview__grid">
            <!-- Left column: status + scores -->
            <div class="overview__main">
                <!-- Hero recommendation card -->
                <div class="hero-card" :class="`hero-card--group-${recommendedGroup}`">
                    <div class="hero-card__label">Recommended starting point</div>
                    <div class="hero-card__headline">
                        <span class="hero-card__group-badge">Group {{ recommendedGroup }}</span>
                        <span class="hero-card__title">{{ recommendedGroupTitle }}</span>
                    </div>
                    <p class="hero-card__reason">{{ recommendedReason }}</p>
                    <p class="hero-card__scope-note">{{ EESA_SCOPE_NOTE }}</p>

                    <div class="hero-card__metrics">
                        <div class="hero-metric">
                            <div class="hero-metric__label">Max Syllables</div>
                            <div class="hero-metric__value">{{ maxMetrics.syllable || '—' }}</div>
                        </div>
                        <div class="hero-metric">
                            <div class="hero-metric__label">Max Different Consonants</div>
                            <div class="hero-metric__value">{{ maxMetrics.consonant || '—' }}</div>
                        </div>
                        <div class="hero-metric">
                            <div class="hero-metric__label">Max Different Vowels</div>
                            <div class="hero-metric__value">{{ maxMetrics.vowel || '—' }}</div>
                        </div>
                    </div>
                </div>

                <!-- No echoic targets indicated: the Guide's alternative, quoted -->
                <NoTargetsPanel v-if="noTargets" />

                <!-- Group scores at-a-glance -->
                <div class="group-scores">
                    <h3 class="group-scores__title">Assessment scores</h3>
                    <div class="group-scores__grid">
                        <div
                            v-for="g in 5"
                            :key="g"
                            class="group-score"
                            :class="{
                                'group-score--active': g === recommendedGroup,
                                'group-score--passed': groupScoresPct[g] >= 80,
                            }"
                        >
                            <div class="group-score__label">Group {{ g }}</div>
                            <div class="group-score__value">{{ formatPct(groupScoresPct[g]) }}</div>
                            <div class="group-score__fraction">{{ groupFraction(g) }}</div>
                            <div class="group-score__bar">
                                <div
                                    class="group-score__bar-fill"
                                    :style="{ width: `${Math.min(groupScoresPct[g] || 0, 100)}%` }"
                                ></div>
                            </div>
                        </div>
                    </div>
                    <div class="group-scores__hint">
                        <span>Need the detailed analysis tables and group-level breakdowns?</span>
                        <div class="group-scores__hint-actions">
                            <a
                                href="javascript:void(0)"
                                class="guided-btn guided-btn--outline"
                                @click="$emit('view-full-packet')"
                            >
                                <span class="material-symbols-outlined">open_in_new</span>
                                Open Full Packet
                            </a>
                            <div
                                v-if="organizationId && learnerId && assessmentId"
                                class="guided-btn guided-btn--primary guided-btn--pdf"
                            >
                                <WorkpacketPdf
                                    :button-label="'Download Full Packet PDF'"
                                    :assessmentId="assessmentId"
                                    :organizationId="organizationId"
                                    :learnerId="learnerId"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Right column: checklist -->
            <aside class="overview__sidebar">
                <div class="checklist">
                    <h3 class="checklist__title">Next steps</h3>
                    <ul class="checklist__list">
                        <li
                            v-for="item in checklistItems"
                            :key="item.id"
                            class="checklist__item"
                            :class="{
                                'checklist__item--done': item.done,
                                'checklist__item--current': item.current,
                                'checklist__item--clickable': !!item.action,
                            }"
                            @click="item.action && item.action()"
                        >
                            <div class="checklist__icon">
                                <span v-if="item.done" class="material-symbols-outlined">check_circle</span>
                                <span v-else-if="item.current" class="material-symbols-outlined">radio_button_checked</span>
                                <span v-else class="material-symbols-outlined">radio_button_unchecked</span>
                            </div>
                            <div class="checklist__text">
                                <div class="checklist__label">{{ item.label }}</div>
                                <div class="checklist__sub">{{ item.sub }}</div>
                            </div>
                            <span v-if="item.action" class="checklist__arrow material-symbols-outlined">chevron_right</span>
                        </li>
                    </ul>
                </div>

                <button
                    type="button"
                    class="overview__cta"
                    @click="$emit('go-to-step', nextActionStep)"
                >
                    {{ nextActionLabel }}
                    <span class="material-symbols-outlined">arrow_forward</span>
                </button>
            </aside>
        </div>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import api from '@/services/axios';
import { handleError } from '@/utils/handleError';
import WorkpacketPdf from '@/components/assesments/workpacket/WorkpacketPdf.vue';
import NoTargetsPanel from '@/components/assesments/NoTargetsPanel.vue';
import { EESA_SCOPE_NOTE } from '@/utils/assessment-notes';
import { noTargetsIndicated } from '@/utils/assessment-targets';

const props = defineProps({
    organizationId: Number,
    learnerId: Number,
    assessmentId: Number,
    assessment: Object,
});

const emit = defineEmits(['go-to-step', 'view-full-packet']);

// Local fresh copy (falls back to prop if fetch hasn't finished yet)
const localAssessment = ref(null);
const effectiveAssessment = computed(() => localAssessment.value || props.assessment);

const refresh = async () => {
    try {
        const { data } = await api.get(
            `/organizations/${props.organizationId}/assessments/${props.assessmentId}`
        );
        localAssessment.value = data?.data || null;
    } catch (error) {
        handleError(error);
    }
};

onMounted(refresh);

const learnerFirstName = computed(() => effectiveAssessment.value?.learner?.firstname || '');
const learnerLastName = computed(() => effectiveAssessment.value?.learner?.lastname || '');
const learnerName = computed(() => {
    const f = learnerFirstName.value;
    const l = learnerLastName.value;
    return [f, l].filter(Boolean).join(' ');
});

// Parse group scores into a clean {1: pct, 2: pct, ...} shape
const groupScoresPct = computed(() => {
    const raw = effectiveAssessment.value?.result?.group_scores || {};
    const out = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
    for (let i = 1; i <= 5; i++) {
        const g = raw[i];
        if (g?.total > 0) {
            out[i] = (g.score_one / g.total) * 100;
        }
    }
    return out;
});

// X/Y fraction display per group
const groupFraction = (g) => {
    const raw = effectiveAssessment.value?.result?.group_scores?.[g];
    if (!raw?.total) return '—';
    return `${raw.score_one ?? 0}/${raw.total}`;
};

const noTargets = computed(() => noTargetsIndicated(groupScoresPct.value));

// Port the "pick active group" algorithm from TargetDecision.vue
const recommendedGroup = computed(() => {
    const s = groupScoresPct.value;
    if (s[1] < 80) return 1;
    for (let i = 2; i <= 5; i++) {
        if (s[i - 1] < 80) return i - 1;
        if (s[i] < 80) return i;
    }
    return 5;
});

const recommendedGroupTitle = computed(() => {
    const titles = {
        1: 'Foundational single sounds',
        2: 'Two-syllable combinations',
        3: 'Longer syllable strings',
        4: 'Multi-word combinations',
        5: 'Complex phrases',
    };
    return titles[recommendedGroup.value];
});

const recommendedReason = computed(() => {
    const s = groupScoresPct.value;
    const g = recommendedGroup.value;

    if (g === 1) {
        return `Group 1 scored ${formatPct(s[1])}, below the 80% mastery threshold. Start here to build a foundation of single-syllable sounds before advancing.`;
    }

    const prev = g - 1;
    const prevScore = formatPct(s[prev]);
    const currScore = formatPct(s[g]);

    if (s[g] >= 80) {
        return `${learnerFirstName.value || 'This learner'} has mastered all five groups (${currScore} on Group ${g}). Targeted echoic intervention may not be needed — see the EESA-R General Information chapter.`;
    }

    return `Group ${prev} is strong (${prevScore}). Group ${g} scored ${currScore} — below mastery, so this is the right level to work on next.`;
});

// Pull max SCV from the result data (groups 1-3 have SCV)
const maxMetrics = computed(() => {
    const averages = effectiveAssessment.value?.result?.scv_averages || {};
    const vals = Object.values(averages);
    if (!vals.length) return { syllable: 0, consonant: 0, vowel: 0 };
    const max = (key) => Math.max(0, ...vals.map(v => parseFloat(v?.[key]) || 0));
    return {
        syllable: max('syllable'),
        consonant: max('consonant'),
        vowel: max('vowel'),
    };
});

// Helpers to determine checklist completion state
const hasPreferences = computed(() => {
    const raw = effectiveAssessment.value?.note?.preferences_list;
    if (!raw) return false;
    try {
        const p = JSON.parse(raw);
        return [p.activities, p.items, p.places].some(v => v && v.trim());
    } catch { return false; }
});

const hasMiniProgramPlan = computed(() => {
    const raw = effectiveAssessment.value?.note?.mini_program_plan;
    if (!raw) return false;
    try {
        const arr = JSON.parse(raw);
        return Array.isArray(arr) && arr.length > 0;
    } catch { return false; }
});

const hasProgramTargets = computed(() => {
    const raw = effectiveAssessment.value?.note?.program_targets;
    if (!raw) return false;
    try {
        const arr = JSON.parse(raw);
        return Array.isArray(arr) && arr.some(t => t.header && t.header.trim());
    } catch { return false; }
});

// Checklist items — order reflects the guided flow
const checklistItems = computed(() => {
    const items = [
        {
            id: 'assessment',
            label: 'Assessment completed',
            sub: 'Test results are in',
            done: true,
            current: false,
            action: null,
        },
        {
            id: 'preferences',
            label: 'Add preferences',
            sub: 'What does the learner like? Informs word choice.',
            done: hasPreferences.value,
            current: !hasPreferences.value,
            action: () => emit('go-to-step', 2),
        },
        {
            id: 'syllables',
            label: 'Review syllable tracker',
            sub: 'Confirm the sounds the learner can already produce',
            done: false,
            current: hasPreferences.value && !hasMiniProgramPlan.value,
            action: () => emit('go-to-step', 2),
        },
        {
            id: 'words',
            label: 'Build word ideas',
            sub: 'Use AI or add custom targets',
            done: hasMiniProgramPlan.value,
            current: hasPreferences.value && !hasMiniProgramPlan.value,
            action: () => emit('go-to-step', 2),
        },
        {
            id: 'targets',
            label: 'Create program targets',
            sub: 'Your final shaping plan',
            done: hasProgramTargets.value,
            current: hasMiniProgramPlan.value && !hasProgramTargets.value,
            action: () => emit('go-to-step', 3),
        },
    ];

    // Ensure only one "current" flag is set — the first undone after assessment
    let foundCurrent = false;
    return items.map(item => {
        if (item.done || item.id === 'assessment') {
            return { ...item, current: false };
        }
        if (!foundCurrent) {
            foundCurrent = true;
            return { ...item, current: true };
        }
        return { ...item, current: false };
    });
});

const nextAction = computed(() => {
    return checklistItems.value.find(i => i.current) || checklistItems.value.find(i => !i.done);
});

const nextActionLabel = computed(() => {
    const a = nextAction.value;
    if (!a) return 'Review program targets';
    if (a.id === 'preferences' || a.id === 'syllables' || a.id === 'words') return `Get started — ${a.label}`;
    if (a.id === 'targets') return 'Build program targets';
    return 'Continue';
});

const nextActionStep = computed(() => {
    const a = nextAction.value;
    if (!a) return 3;
    return a.id === 'targets' ? 3 : 2;
});

function formatPct(value) {
    if (value === null || value === undefined) return '—';
    const num = Number(value);
    if (!isFinite(num)) return '—';
    return `${Math.round(num)}%`;
}
</script>

<style scoped>
.overview {
    background: transparent;
}

.overview__greeting {
    margin-bottom: 24px;
}

.overview__greeting-title {
    font-size: 28px;
    font-weight: 700;
    color: #222;
    margin: 0;
}

.overview__greeting-sub {
    color: #666;
    margin: 4px 0 0;
    font-size: 15px;
}

.overview__grid {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    gap: 20px;
    align-items: start;
}

@media (max-width: 1024px) {
    .overview__grid {
        grid-template-columns: 1fr;
    }
}

.overview__main {
    display: flex;
    flex-direction: column;
    gap: 20px;
    min-width: 0;
}

.overview__sidebar {
    display: flex;
    flex-direction: column;
    gap: 16px;
    position: sticky;
    top: 24px;
}

/* Hero card */
.hero-card {
    background: linear-gradient(135deg, #725CFF 0%, #9d8bff 100%);
    color: #fff;
    border-radius: 16px;
    padding: 32px;
    box-shadow: 0 8px 24px rgba(114, 92, 255, 0.25);
}

.hero-card--group-1 { background: linear-gradient(135deg, #725CFF 0%, #9d8bff 100%); }
.hero-card--group-2 { background: linear-gradient(135deg, #4AA3FF 0%, #7dbfff 100%); }
.hero-card--group-3 { background: linear-gradient(135deg, #F2B134 0%, #ffd074 100%); }
.hero-card--group-4 { background: linear-gradient(135deg, #E8821C 0%, #ffa94d 100%); }
.hero-card--group-5 { background: linear-gradient(135deg, #1E4D8C 0%, #4977c2 100%); }

.hero-card__label {
    text-transform: uppercase;
    letter-spacing: 0.8px;
    font-size: 11px;
    font-weight: 600;
    opacity: 0.85;
    margin-bottom: 10px;
}

.hero-card__headline {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 12px;
    flex-wrap: wrap;
}

.hero-card__group-badge {
    background: rgba(255, 255, 255, 0.22);
    padding: 4px 12px;
    border-radius: 20px;
    font-weight: 600;
    font-size: 13px;
    backdrop-filter: blur(6px);
}

.hero-card__title {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.2;
}

.hero-card__reason {
    font-size: 14px;
    line-height: 1.55;
    opacity: 0.95;
    margin: 0 0 12px;
}

.hero-card__scope-note {
    font-size: 12px;
    line-height: 1.5;
    opacity: 0.85;
    margin: 0 0 24px;
}

.hero-card__metrics {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
}

.hero-metric {
    background: rgba(255, 255, 255, 0.15);
    border-radius: 10px;
    padding: 14px;
    backdrop-filter: blur(6px);
}

.hero-metric__label {
    font-size: 11px;
    opacity: 0.85;
    margin-bottom: 6px;
}

.hero-metric__value {
    font-size: 22px;
    font-weight: 700;
}

/* Group scores */
.group-scores {
    background: #fff;
    border-radius: 12px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(114, 92, 255, 0.06);
}

.group-scores__title {
    font-size: 16px;
    font-weight: 600;
    color: #222;
    margin: 0 0 16px;
}

.group-scores__grid {
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 10px;
}

@media (max-width: 640px) {
    .group-scores__grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

.group-score {
    padding: 14px 10px;
    border-radius: 10px;
    background: #f7f6ff;
    text-align: center;
    transition: transform 0.15s;
}

.group-score--active {
    background: #725CFF;
    color: #fff;
    transform: scale(1.04);
    box-shadow: 0 4px 12px rgba(114, 92, 255, 0.3);
}

.group-score--passed:not(.group-score--active) {
    background: #e8f9f0;
}

.group-score__label {
    font-size: 12px;
    font-weight: 500;
    opacity: 0.7;
    margin-bottom: 4px;
}

.group-score--active .group-score__label {
    opacity: 0.9;
}

.group-score__value {
    font-size: 22px;
    font-weight: 700;
    margin-bottom: 6px;
}

.group-score__bar {
    height: 4px;
    background: rgba(0, 0, 0, 0.08);
    border-radius: 2px;
    overflow: hidden;
}

.group-score--active .group-score__bar {
    background: rgba(255, 255, 255, 0.25);
}

.group-score__bar-fill {
    height: 100%;
    background: #36C27A;
    border-radius: 2px;
    transition: width 0.3s;
}

.group-score--active .group-score__bar-fill {
    background: #fff;
}

.group-score__fraction {
    font-size: 11px;
    font-weight: 500;
    color: #888;
    margin-bottom: 6px;
}

.group-score--active .group-score__fraction {
    color: rgba(255, 255, 255, 0.85);
}

.group-scores__hint {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    font-size: 13px;
    color: #666;
    margin: 20px 0 0;
    text-align: center;
}

.group-scores__hint-actions {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: center;
}

/* Shared button styles */
.guided-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    cursor: pointer;
    border: none;
    line-height: 1;
    transition: transform 0.1s, box-shadow 0.15s, background 0.15s;
}

.guided-btn--outline {
    background: #fff;
    color: #725CFF;
    border: 1.5px solid #725CFF;
}

.guided-btn--outline:hover {
    background: #f7f6ff;
    transform: translateY(-1px);
}

.guided-btn--primary {
    background: #725CFF;
    color: #fff;
    box-shadow: 0 3px 8px rgba(114, 92, 255, 0.3);
    padding: 0;
}

.guided-btn--primary:hover {
    background: #5d47e0;
    transform: translateY(-1px);
    box-shadow: 0 5px 12px rgba(114, 92, 255, 0.4);
}

.guided-btn .material-symbols-outlined {
    font-size: 18px;
    line-height: 1;
}

/* Override WorkpacketPdf's inner anchor to match the button shell it's wrapped in */
.guided-btn--pdf :deep(.download-workpacket-pdf) {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    color: #fff;
    font-size: 14px;
    font-weight: 600;
    text-decoration: none;
    border-radius: 10px;
}

.guided-btn--pdf :deep(.btn__icon) {
    display: inline-flex;
    align-items: center;
}

.guided-btn--pdf :deep(.btn__icon img) {
    width: 16px;
    height: 16px;
    filter: brightness(0) invert(1);
}

.guided-btn--pdf :deep(.btn__text) {
    font-size: 14px;
    font-weight: 600;
    color: #fff;
}

/* Checklist */
.checklist {
    background: #fff;
    border-radius: 12px;
    padding: 20px;
    box-shadow: 0 2px 8px rgba(114, 92, 255, 0.06);
}

.checklist__title {
    font-size: 14px;
    font-weight: 600;
    color: #222;
    margin: 0 0 14px;
    text-transform: uppercase;
    letter-spacing: 0.6px;
}

.checklist__list {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.checklist__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 10px;
    border-radius: 8px;
    transition: background 0.15s;
}

.checklist__item--clickable { cursor: pointer; }

.checklist__item--clickable:hover { background: #f7f6ff; }

.checklist__item--current {
    background: #f0edff;
}

.checklist__icon {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #bbb;
}

.checklist__item--done .checklist__icon { color: #36C27A; }

.checklist__item--current .checklist__icon { color: #725CFF; }

.checklist__text { flex: 1; min-width: 0; }

.checklist__label {
    font-weight: 500;
    font-size: 14px;
    color: #222;
    line-height: 1.3;
}

.checklist__item--done .checklist__label {
    color: #888;
    text-decoration: line-through;
    text-decoration-color: #ccc;
}

.checklist__sub {
    font-size: 12px;
    color: #888;
    margin-top: 2px;
    line-height: 1.3;
}

.checklist__arrow {
    color: #ccc;
    font-size: 20px;
}

.checklist__item--current .checklist__arrow { color: #725CFF; }

/* CTA */
.overview__cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background: #725CFF;
    color: #fff;
    border: none;
    padding: 14px 20px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(114, 92, 255, 0.3);
    transition: transform 0.15s, box-shadow 0.15s;
    width: 100%;
}

.overview__cta:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(114, 92, 255, 0.4);
}

.overview__cta .material-symbols-outlined {
    font-size: 18px;
}
</style>
